import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Table,
  TableRow,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  TablePagination,
  IconButton,
  Stack,
  Menu,
  MenuItem,
  Typography,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import ReactCountryFlag from 'react-country-flag';
import PropTypes from 'prop-types';
import Iconify from '../common/iconify.component';
import Sortable from './sortable.component';
import NoData from './no-data.component';
import Error from './error.component';
import Loading from './loading.component';
import CellValue from './cell-value.component';
import CELL_DATA_TYPES from '../../config/cell-types.config';
import { ACTIONS, DEFAULT_FEATURED_LESSON_ID } from '../../config/const.config';

const ListData = ({
  columns,
  rows,
  page,
  rowsPerPage,
  totalRows,
  loading,
  actions,
  error,
  sortBy,
  sortOrder,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  onAction,
  onCustomClick,
  handleImagePreview,
  handleToggleStatus,
  handleToggleFeatured,
  getActionLoadingState,
}) => {
  const colSpan = columns.length + (actions.length ? 1 : 0);

  const [anchorActionEl, setAnchorActionEl] = useState(null);
  const [openActions, setOpenActions] = useState({
    show: false,
    data: null,
  });

  const { primaryActions, otherActions } = useMemo(() => {
    const primary = [];
    const other = [];

    actions.forEach((a) => {
      if (
        [
          ACTIONS.WPEDIT.value,
          ACTIONS.EDIT.value,
          ACTIONS.VIEW.value,
          ACTIONS.DELETE.value,
        ].includes(a.action)
      ) {
        primary.push(a);
      } else {
        other.push(a);
      }
    });

    return {
      primaryActions: primary,
      otherActions: other,
    };
  }, [actions]);

  const handleActionOpen = (e, selectedRow) => {
    setAnchorActionEl(e.currentTarget);
    setOpenActions({
      ...openActions,
      show: true,
      data: selectedRow,
    });
  };
  const handleActionClose = () => {
    setAnchorActionEl(null);
    setOpenActions({
      ...openActions,
      show: false,
      data: null,
    });
  };
  const handleActionSelect = (selectedAction) => {
    onAction(selectedAction, openActions.data);
    handleActionClose();
  };
  const handleSingleActionSelect = (data) => {
    onAction(actions[0].action, data);
    handleActionClose();
  };

  const getRowKey = (row) =>
    [row.id, row.assetType, row.languageId, row.fileName, row.title, row.slug]
      .filter((value) => value !== undefined && value !== null && value !== '')
      .join('-');

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell
                  key={`col-${c.id}`}
                  align={c.align}
                  width={c.width}
                  sx={{
                    cursor: c.sortable ? 'pointer' : 'inherit',
                    bgcolor: '#F4F6F8',
                    color: '#637381',
                    fontWeight: 600,
                    paddingY: '6px',
                    fontSize: '13px',
                  }}
                  onClick={() => {
                    if (c.sortable) {
                      if (c.dataKey === sortBy) {
                        if (sortOrder === '') {
                          onSortChange(c.dataKey, 'asc');
                        } else if (sortOrder === 'asc') {
                          onSortChange(c.dataKey, 'desc');
                        } else if (sortOrder === 'desc') {
                          onSortChange('', '');
                        }
                      } else {
                        onSortChange(c.dataKey, 'asc');
                      }
                    }
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent={c.align}
                  >
                    {c.label}
                    {c.sortable && (
                      <Sortable
                        dataKey={c.dataKey}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                      />
                    )}
                  </Stack>
                </TableCell>
              ))}

              {actions.length > 0 && (
                <TableCell
                  align="center"
                  width="5%"
                  sx={{
                    bgcolor: '#F4F6F8',
                    color: '#637381',
                    fontWeight: 600,
                  }}
                >
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {!loading && !error && (
              <>
                {rows.map((r) => (
                  <TableRow key={`row-${getRowKey(r)}`}>
                    {columns.map((c) => (
                      <TableCell
                        key={`data-col-${getRowKey(r)}-${c.id}`}
                        align={c.align}
                        width={c.width}
                        sx={{
                          paddingY: '8px',
                          fontSize: '13px',
                          ...(c.dataKey === 'preview' ||
                          c.dataKey === 'bannerImageUrl' ||
                          c.dataKey === 'thumbnail'
                            ? {
                                cursor: 'pointer',
                              }
                            : {}),
                        }}
                        onClick={() => {
                          if (
                            c.dataKey === 'preview' &&
                            r.videoUrl &&
                            r.cdnStatus === 'Finished'
                          ) {
                            onCustomClick(r);
                          } else if (
                            (c.dataKey === 'thumbnail' && r.thumbnail) ||
                            (c.dataKey === 'bannerImageUrl' && r.bannerImageUrl)
                          ) {
                            handleImagePreview(r);
                          } else if (c.dataKey === 'fileUrl') {
                            onCustomClick(r.fileUrl);
                          }
                        }}
                      >
                        <CellValue
                          type={c.type}
                          value={r[c.dataKey]}
                          cdnStatus={r.cdnStatus}
                          fallbackValue={(e) => {
                            if (
                              c.dataKey === 'status' &&
                              c.type === CELL_DATA_TYPES.SWITCH
                            ) {
                              return handleToggleStatus(r, e.target.checked);
                            }
                            if (c.dataKey === 'isFeatured') {
                              return handleToggleFeatured(r, e.target.checked);
                            }
                            return null;
                          }}
                          disabledToggle={
                            ((c.dataKey === 'status' && r.isFeatured) ||
                              (c.dataKey === 'isFeatured' && !r.status) ||
                              (c.dataKey === 'isFeatured' &&
                                r.isFeatured &&
                                r.id === DEFAULT_FEATURED_LESSON_ID)) &&
                            true
                          }
                        />

                        {c.id === 'countryName' && r[c.dataKey] !== '' && (
                          <Box display="inline">
                            {' '}
                            <ReactCountryFlag
                              countryCode={r.countryCode}
                              title={r.countryName}
                              style={{
                                fontSize: '1.3em',
                              }}
                            />
                          </Box>
                        )}

                        {c.secondaryFields && (
                          <>
                            {c.secondaryFields.map((sf, sfIdx) => (
                              <React.Fragment
                                key={`sf-data-col-${c.id}-${sfIdx}`}
                              >
                                <br />
                                {r[sf.dataKey] && (
                                  <CellValue
                                    type={sf.type}
                                    value={`${sf.label ?? ''}${
                                      r[sf.dataKey] ?? ''
                                    }`}
                                    color="grey.500"
                                  />
                                )}
                              </React.Fragment>
                            ))}
                          </>
                        )}
                      </TableCell>
                    ))}

                    {actions.length > 0 && (
                      <TableCell
                        align="right"
                        width="18%"
                        sx={{ paddingY: '8px' }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="center"
                          alignItems="center"
                        >
                          {primaryActions.map((a) => (
                            <>
                              {a.action === ACTIONS.WPEDIT.value && (
                                <Link
                                  target="_blank"
                                  to={`https://www.torahclass.com/wp-admin/edit.php?post_type=tc-lessons&page=tc-lessons-import&lesson_id=${r.id}`}
                                  style={{ textDecoration: 'none' }}
                                >
                                  <Iconify
                                    icon="ic:baseline-wordpress"
                                    width={20}
                                    height={20}
                                    color="#067ff7"
                                    style={{ marginRight: '10px' }}
                                  />
                                </Link>
                              )}
                              {a.action !== ACTIONS.WPEDIT.value && (
                                <IconButton
                                  size="small"
                                  color={
                                    a.action === ACTIONS.DELETE.value
                                      ? 'error'
                                      : 'inherit'
                                  }
                                  sx={{
                                    color:
                                      a.action !== ACTIONS.DELETE.value
                                        ? '#919EAB'
                                        : undefined,
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onAction(a.action, r);
                                  }}
                                  title={
                                    // eslint-disable-next-line no-nested-ternary
                                    a.action === ACTIONS.EDIT.value
                                      ? ACTIONS.EDIT.label
                                      : a.action === ACTIONS.VIEW.value
                                      ? ACTIONS.VIEW.label
                                      : ACTIONS.DELETE.label
                                  }
                                >
                                  <Iconify
                                    icon={
                                      // eslint-disable-next-line no-nested-ternary
                                      a.action === ACTIONS.EDIT.value
                                        ? 'eva:edit-fill'
                                        : a.action === ACTIONS.VIEW.value
                                        ? 'eva:eye-fill'
                                        : 'eva:trash-2-outline'
                                    }
                                  />
                                </IconButton>
                              )}
                            </>
                          ))}

                          {otherActions
                            .filter((a) => a.icon)
                            .map((act) => {
                              const isActionLoading = getActionLoadingState(
                                act.action,
                                r
                              );

                              return (
                                <IconButton
                                  key={`action-${act.action}-${r.id}`}
                                  size="small"
                                  color="inherit"
                                  disabled={isActionLoading}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onAction(act.action, r);
                                  }}
                                  title={act.label}
                                >
                                  {isActionLoading ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <Iconify icon={act.icon} />
                                  )}
                                </IconButton>
                              );
                            })}

                          {otherActions.filter((a) => !a.icon).length > 0 && (
                            <IconButton
                              size="small"
                              color="inherit"
                              onClick={(e) => handleActionOpen(e, r)}
                            >
                              <Iconify icon="eva:more-vertical-fill" />
                            </IconButton>
                          )}
                        </Stack>
                      </TableCell>
                    )}

                    {actions.length === 1 && otherActions.length > 0 && (
                      <TableCell
                        align="right"
                        width="5%"
                        sx={{ paddingY: '8px' }}
                      >
                        {actions[0].icon ? (
                          <IconButton
                            size="small"
                            color="inherit"
                            onClick={() => handleSingleActionSelect(r)}
                            title={actions[0].label}
                          >
                            <Iconify icon={actions[0].icon} />
                          </IconButton>
                        ) : (
                          <Button
                            size="small"
                            color="info"
                            variant="contained"
                            onClick={() => handleSingleActionSelect(r)}
                          >
                            {actions[0].label}
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </>
            )}

            {!loading && !error && totalRows === 0 && (
              <TableRow>
                <TableCell
                  align="center"
                  colSpan={colSpan}
                  sx={{ paddingY: '12px' }}
                >
                  <NoData />
                </TableCell>
              </TableRow>
            )}

            {!loading && error && totalRows === 0 && (
              <TableRow>
                <TableCell
                  align="center"
                  colSpan={colSpan}
                  sx={{ paddingY: '12px' }}
                >
                  <Error />
                </TableCell>
              </TableRow>
            )}

            {loading && (
              <TableRow>
                <TableCell
                  align="center"
                  colSpan={colSpan}
                  sx={{ paddingY: '12px' }}
                >
                  <Loading />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && totalRows > 0 && (
        <TablePagination
          component="div"
          count={totalRows}
          page={page}
          onPageChange={(e, newPage) => onPageChange(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            onRowsPerPageChange(Number(e.target.value));
          }}
        />
      )}

      {openActions.show && (
        <Menu
          id="actions-menu"
          anchorEl={anchorActionEl}
          open
          onClose={() => handleActionClose()}
          size="small"
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {otherActions.map((a, index) => {
            const shouldRender = a.conditional
              ? a.conditional(openActions.data)
              : true;

            if (!shouldRender) return null;

            return (
              <MenuItem
                key={`mia-${a.action}-${index}`}
                onClick={() => handleActionSelect(a.action)}
              >
                <Typography variant="body2">{a.label}</Typography>
              </MenuItem>
            );
          })}
        </Menu>
      )}
    </>
  );
};

ListData.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  totalRows: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  actions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  error: PropTypes.bool.isRequired,
  sortBy: PropTypes.string.isRequired,
  sortOrder: PropTypes.string.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  onAction: PropTypes.func,
  onCustomClick: PropTypes.func,
  handleImagePreview: PropTypes.func,
  handleToggleStatus: PropTypes.func,
  handleToggleFeatured: PropTypes.func,
  getActionLoadingState: PropTypes.func,
};
ListData.defaultProps = {
  onAction: () => {},
  onCustomClick: () => {},
  handleImagePreview: () => {},
  handleToggleStatus: () => {},
  handleToggleFeatured: () => {},
  getActionLoadingState: () => {},
};

export default ListData;
